import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import { fetchAnomaly, fetchSources } from "../../lib/api";
import { formatCoords, formatEventDate, TYPE_LABELS } from "../../lib/format";
import type { LunarEvent, Source } from "../../types";

const EventDetail = () => {
  const { id } = useParams();
  const [event, setEvent] = useState<LunarEvent | null>(null);
  const [source, setSource] = useState<Source | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    Promise.all([fetchAnomaly(id), fetchSources()])
      .then(([item, sources]) => {
        if (cancelled) return;
        setEvent(item);
        setSource(sources.find((entry) => entry.id === item.sourceId) ?? null);
        document.title = `${item.featureName ?? item.locationText} · ${formatEventDate(item)} | Lunamalies`;
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <Layout>
      <section className="max-w-3xl mx-auto px-6 py-12 w-full">
        <Link to="/catalog" className="text-turquoise hover:text-mars text-sm">
          ← Back to catalog
        </Link>

        {error && <p className="text-mars mt-6">{error}</p>}
        {!event && !error && <p className="mt-6">Loading…</p>}

        {event && (
          <article className="mt-6">
            <p className="text-sun font-semibold tracking-wide uppercase text-sm">
              {TYPE_LABELS[event.phenomenonTypeId]}
            </p>
            <h1 className="text-4xl font-bold text-alt-blk mt-2">
              {event.featureName ?? event.locationText}
            </h1>
            <p className="text-xl text-alt-blk/70 mt-2">{formatEventDate(event)}</p>

            <dl className="grid sm:grid-cols-2 gap-4 mt-8 text-sm">
              <div className="rounded-2xl bg-white p-4 border border-alt-blk/10">
                <dt className="text-alt-blk/50">Location in catalog</dt>
                <dd className="mt-1 font-medium">{event.locationText}</dd>
              </div>
              <div className="rounded-2xl bg-white p-4 border border-alt-blk/10">
                <dt className="text-alt-blk/50">Selenographic coordinates</dt>
                <dd className="mt-1 font-medium">
                  {formatCoords(event.latitude, event.longitude)}
                </dd>
              </div>
              <div className="rounded-2xl bg-white p-4 border border-alt-blk/10">
                <dt className="text-alt-blk/50">Observer</dt>
                <dd className="mt-1 font-medium">{event.observer ?? "Not recorded"}</dd>
              </div>
              <div className="rounded-2xl bg-white p-4 border border-alt-blk/10">
                <dt className="text-alt-blk/50">Duration</dt>
                <dd className="mt-1 font-medium">{event.durationText ?? "Not recorded"}</dd>
              </div>
            </dl>

            <div className="mt-8 rounded-2xl bg-white p-6 border border-alt-blk/10">
              <h2 className="text-lg font-bold mb-2">Description</h2>
              <p className="leading-relaxed">{event.description}</p>
            </div>

            <div className="mt-6 rounded-2xl bg-white p-6 border border-alt-blk/10">
              <h2 className="text-lg font-bold mb-2">Source</h2>
              <p className="text-sm">{event.reference}</p>
              {source && (
                <p className="text-sm text-alt-blk/70 mt-3">
                  {source.citation}{" "}
                  {source.url && (
                    <a
                      href={source.url}
                      className="text-turquoise hover:text-mars"
                      target="_blank"
                      rel="noreferrer"
                    >
                      NTRS record
                    </a>
                  )}
                </p>
              )}
              {event.typeInferred && (
                <p className="text-xs text-alt-blk/50 mt-4">
                  Phenomenon type is inferred from the description using Cameron’s five
                  classes. TR R-277 itself does not assign those classes.
                </p>
              )}
            </div>

            {event.featureId && (
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to={`/?feature=${event.featureId}`}
                  className="inline-block bg-mars hover:bg-sunset text-white px-6 py-3 rounded-2xl font-semibold"
                >
                  View on the map
                </Link>
                <Link
                  to={`/catalog?feature=${event.featureId}`}
                  className="inline-block border border-alt-blk px-6 py-3 rounded-2xl font-semibold hover:bg-alt-blk hover:text-white transition-colors"
                >
                  Other reports at this feature
                </Link>
              </div>
            )}
          </article>
        )}
      </section>
    </Layout>
  );
};

export default EventDetail;
